import { customContract, type PlatformContract, platformById, platforms, withFormat } from "@ondit/publishing";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import type { PublishingPlatform } from "~/core/api/distribution";
import { api } from "~/core/api/index";
import { keys } from "~/core/query";

/**
 * Every contract a brand can publish under: the registry, then the platforms
 * the brand added on Settings. The Distribution list also carries the bundled
 * reference platforms that have no contract; only the brand's own rows (the
 * server names them `custom_…`) are drawn as one.
 */
export function brandContracts(list: PublishingPlatform[] = []) {
	const custom = list.filter((p) => p.id.startsWith("custom_") && !platformById(p.id)).map(customContract);
	const all: PlatformContract[] = [...platforms, ...custom];
	const byId = (id: string) => platformById(id) ?? custom.find((p) => p.id === id);
	return {
		all,
		byId,
		/** One kind of post on the platform; the default when `format` is empty. */
		kind: (id: string, format?: string | null) => {
			const p = byId(id);
			return p && withFormat(p, format);
		},
	};
}

export function useBrandContracts(entityId: string) {
	const list = useQuery({
		queryKey: [...keys.entity(entityId), "distribution"],
		queryFn: () => api.distribution(entityId),
		enabled: !!entityId,
	});
	return useMemo(() => brandContracts(list.data?.platforms), [list.data]);
}
